import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2, Copy, Eye, Users, Target, DollarSign, Package } from 'lucide-react';
import { analyticalLinksApi } from '../../api/endpoints/analyticalLinks';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Textarea } from '../../components/ui/Textarea';
import { Modal } from '../../components/ui/Modal';
import { Pagination } from '../../components/ui/Pagination';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { EmptyState } from '../../components/ui/EmptyState';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { useToast } from '../../components/ui/Alert';
import { CurrencyDisplay } from '../../components/ui/CurrencyDisplay';
import { usePagination } from '../../hooks/usePagination';
import { formatDate } from '../../lib/formatters';
import { useStore } from '../../contexts/StoreContext';
import type { AnalyticalLink, CreateAnalyticalLinkInput } from '../../types/analyticalLink';

export function AnalyticalLinkList() {
  const { storeId } = useParams<{ storeId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { page, limit, goToPage } = usePagination();
  const { currentStore } = useStore();

  const buildTrackingUrl = (link: AnalyticalLink): string => {
    const domain = currentStore?.domain || `${storeId}.00stores.com`;
    const shortCode = link.shortCode || link.url || link._id;
    return `https://${domain}/?link=${shortCode}`;
  };

  const [showModal, setShowModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<AnalyticalLink | null>(null);
  const [ordersTarget, setOrdersTarget] = useState<AnalyticalLink | null>(null);
  const [formData, setFormData] = useState<CreateAnalyticalLinkInput>({ name: '', url: '', description: '', utmSource: '', utmMedium: '', utmCampaign: '' });

  const { data, isLoading } = useQuery({
    queryKey: ['analytical-links', storeId, { page, limit }],
    queryFn: () => analyticalLinksApi.list(storeId!, { page, limit }),
    enabled: !!storeId,
  });

  const { data: ordersData, isLoading: ordersLoading } = useQuery({
    queryKey: ['analytical-link-orders', storeId, ordersTarget?._id],
    queryFn: () => analyticalLinksApi.getOrders(storeId!, ordersTarget!._id),
    enabled: !!storeId && !!ordersTarget,
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateAnalyticalLinkInput) => analyticalLinksApi.create(storeId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['analytical-links', storeId] });
      toast('success', t('common.success'));
      setShowModal(false);
      setFormData({ name: '', url: '', description: '', utmSource: '', utmMedium: '', utmCampaign: '' });
    },
    onError: () => toast('error', t('common.error')),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => analyticalLinksApi.delete(storeId!, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['analytical-links', storeId] });
      toast('success', t('common.success'));
      setDeleteTarget(null);
    },
    onError: () => toast('error', t('common.error')),
  });

  const links = data?.links ?? [];
  const total = data?.total ?? links.length;
  const totalPages = Math.ceil(total / limit) || 1;

  if (isLoading) return <LoadingSpinner fullPage />;

  return (
    <div>
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-xl font-bold">{t('sidebar.analyticalLinks')}</h1>
        <Button onClick={() => setShowModal(true)}>
          <Plus className="h-4 w-4" />
          {t('analyticalLinks.addLink')}
        </Button>
      </div>

      {links.length === 0 ? (
        <EmptyState title={t('common.noData')} description={t('analyticalLinks.empty')} />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {links.map((link) => (
            <div key={link._id} className="rounded-xl border border-border-default bg-bg-secondary p-5">
              <h3 className="font-medium mb-1 truncate">{link.name}</h3>
              <p className="text-xs text-text-muted mb-3 truncate" dir="ltr">{buildTrackingUrl(link)}</p>
              <div className="grid grid-cols-2 gap-2 mb-3">
                <div className="flex items-center gap-1.5 text-sm">
                  <Eye className="h-3.5 w-3.5 text-accent shrink-0" />
                  <span className="font-medium">{link.visits ?? link.clicks ?? 0}</span>
                  <span className="text-text-muted text-xs truncate">{t('analyticalLinks.visits', 'Visits')}</span>
                </div>
                <div className="flex items-center gap-1.5 text-sm">
                  <Users className="h-3.5 w-3.5 text-accent shrink-0" />
                  <span className="font-medium">{link.uniqueVisitors ?? link.uniqueClicks ?? 0}</span>
                  <span className="text-text-muted text-xs truncate">{t('analyticalLinks.uniqueVisitors', 'Unique')}</span>
                </div>
                <div className="flex items-center gap-1.5 text-sm">
                  <Target className="h-3.5 w-3.5 text-accent shrink-0" />
                  <span className="font-medium">{link.conversions ?? 0}</span>
                  <span className="text-text-muted text-xs truncate">{t('analyticalLinks.conversions', 'Conversions')}</span>
                </div>
                <div className="flex items-center gap-1.5 text-sm">
                  <DollarSign className="h-3.5 w-3.5 text-accent shrink-0" />
                  <CurrencyDisplay amount={link.revenue ?? 0} className="font-medium" />
                </div>
              </div>
              <p className="text-xs text-text-muted mb-3">{link.createdAt ? formatDate(link.createdAt) : ''}</p>
              <div className="flex items-center gap-1 border-t border-border-default pt-3">
                <button
                  onClick={() => { navigator.clipboard.writeText(buildTrackingUrl(link)); toast('success', t('paylinks.linkCopied')); }}
                  className="rounded-lg p-1.5 text-text-muted hover:bg-bg-hover hover:text-accent"
                >
                  <Copy className="h-4 w-4" />
                </button>
                {(link.conversions ?? 0) > 0 && (
                  <button
                    onClick={() => setOrdersTarget(link)}
                    className="rounded-lg p-1.5 text-text-muted hover:bg-bg-hover hover:text-accent"
                    title={t('analyticalLinks.viewOrders', 'View Orders')}
                  >
                    <Package className="h-4 w-4" />
                  </button>
                )}
                <div className="flex-1" />
                <button
                  onClick={() => setDeleteTarget(link)}
                  className="rounded-lg p-1.5 text-text-muted hover:bg-bg-hover hover:text-danger"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <Pagination
          page={page}
          totalPages={totalPages}
          total={total}
          limit={limit}
          onPageChange={goToPage}
        />
      )}

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={t('analyticalLinks.addLink')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowModal(false)}>{t('common.cancel')}</Button>
            <Button
              onClick={() => createMutation.mutate(formData)}
              isLoading={createMutation.isPending}
              disabled={!formData.name || !formData.url}
            >
              {t('common.save')}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Input label={t('common.name')} required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
          <Input label="URL" required type="url" value={formData.url} onChange={(e) => setFormData({ ...formData, url: e.target.value })} />
          <Textarea
            label={t('common.description')}
            value={formData.description ?? ''}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            rows={3}
          />

          <div>
            <p className="mb-3 text-xs font-medium uppercase tracking-wide text-text-muted">{t('analyticalLinks.utmParameters')}</p>
            <div className="space-y-4">
              <Input
                label={t('analyticalLinks.utmSource')}
                value={formData.utmSource ?? ''}
                onChange={(e) => setFormData({ ...formData, utmSource: e.target.value })}
                placeholder="e.g., facebook, google, newsletter"
              />
              <Input
                label={t('analyticalLinks.utmMedium')}
                value={formData.utmMedium ?? ''}
                onChange={(e) => setFormData({ ...formData, utmMedium: e.target.value })}
                placeholder="e.g., cpc, email, social"
              />
              <Input
                label={t('analyticalLinks.utmCampaign')}
                value={formData.utmCampaign ?? ''}
                onChange={(e) => setFormData({ ...formData, utmCampaign: e.target.value })}
                placeholder="e.g., spring_sale, launch_2024"
              />
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title={t('common.confirm')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setDeleteTarget(null)}>{t('common.cancel')}</Button>
            <Button variant="danger" isLoading={deleteMutation.isPending} onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget._id)}>
              {t('common.delete')}
            </Button>
          </>
        }
      >
        <p className="text-sm text-text-secondary">{t('analyticalLinks.deleteConfirm')}</p>
        <p className="mt-2 font-medium">{deleteTarget?.name}</p>
      </Modal>

      <Modal
        isOpen={!!ordersTarget}
        onClose={() => setOrdersTarget(null)}
        title={`${t('analyticalLinks.viewOrders', 'View Orders')} - ${ordersTarget?.name ?? ''}`}
        size="lg"
      >
        {ordersLoading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner />
          </div>
        ) : !ordersData?.orders?.length ? (
          <p className="py-6 text-center text-sm text-text-muted">{t('common.noData')}</p>
        ) : (
          <div className="overflow-x-auto -mx-6">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border-default text-left text-xs font-medium uppercase tracking-wide text-text-muted">
                  <th className="px-6 py-2">{t('orders.orderNumber', 'Order #')}</th>
                  <th className="px-6 py-2">{t('orders.customer', 'Customer')}</th>
                  <th className="px-6 py-2">{t('orders.total', 'Total')}</th>
                  <th className="px-6 py-2">{t('common.date', 'Date')}</th>
                  <th className="px-6 py-2">{t('common.status', 'Status')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-default">
                {ordersData.orders.map((order) => (
                  <tr
                    key={order._id}
                    onClick={() => {
                      setOrdersTarget(null);
                      navigate(`/${storeId}/orders/${order._id}`);
                    }}
                    className="cursor-pointer hover:bg-bg-hover transition-colors"
                  >
                    <td className="px-6 py-3 font-medium">
                      #{order.orderNumber ?? order._id.slice(-6)}
                    </td>
                    <td className="px-6 py-3 text-text-secondary">
                      {order.customer?.name || order.customer?.email || '-'}
                    </td>
                    <td className="px-6 py-3">
                      <CurrencyDisplay amount={order.total} currency={order.currency} />
                    </td>
                    <td className="px-6 py-3 text-text-secondary">
                      {order.createdAt ? formatDate(order.createdAt) : '-'}
                    </td>
                    <td className="px-6 py-3">
                      <StatusBadge status={order.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Modal>
    </div>
  );
}
